import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import { Spinner } from '@salesforce/design-system-react';

import { AssetBrowser } from '../';
import './AssetTable.scss';

class AssetTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedAssets: []
    };
  }

  columns() {
    return [
      { name: 'expand', sortable: false, width: '20px' },
      { name: 'thumbnail', sortable: false, width: '62px' },
      { name: 'key', sortable: false },
      { name: 'type', sortable: false },
      { name: 'isFallback', sortable: false, width: '150px' },
      { name: 'clicked', sortable: false, width: '150px' }
    ];
  }

  filteredAssets() {
    const assets = this.jobAssets(this.props.job);

    return assets.filter(asset => {
      // only include child asset if its parent is expanded
      if (
        asset.parent_id &&
        !this.state.expandedAssets.includes(asset.parent_id)
      ) {
        return false;
      }

      // all filters succeeded without eliminating the asset, include it
      return true;
    });
  }

  // Need to sort assets to put children directly underneath parent when expanded
  sortAssets(assetList) {
    // find all child assets (assets that have a direct parent)
    const children = assetList.filter(asset => !!asset.parent_id);

    // find all non-children (they could be a parent or they could not have any children)
    const parents = assetList.filter(asset => !asset.parent_id);

    // group all children in a map keyed by the parent (e.g. parent1 => [child1, child2])
    const childrenMap = {};
    children.forEach(asset => {
      const hashEntry = childrenMap[asset.parent_id] || [];
      childrenMap[asset.parent_id] = [...hashEntry, asset];
    });

    // take all parents (ie non-children) and one by one put them into a new array
    // and after each parent insert each child asset immediately after the parent
    // Note: children of un-expanded parents will be filtered out already
    const assets = [];
    parents.forEach(parent => {
      assets.push(parent);
      const directChildren = childrenMap[parent.uuid] || [];
      assets.push(...directChildren);
    });
    return assets;
  }

  handleTableAction(action, item) {
    switch (action) {
      case 'toggleExpand':
        let toggledAssets;
        const { expandedAssets } = this.state;
        if (expandedAssets.includes(item.uuid)) {
          toggledAssets = expandedAssets.filter(uuid => item.uuid !== uuid);
        } else {
          toggledAssets = [...expandedAssets, item.uuid];
        }
        this.setState({ expandedAssets: toggledAssets });
        break;
      case 'openDetails':
        this.props.actions.viewSelection(
          this.props.subscriber,
          item.jobId,
          item.blockId
        );
        this.props.history.push(
          `/selections/${this.props.subscriber}/${item.jobId}/${item.blockId}`
        );
        break;
      default:
        break;
    }
  }

  jobAssets(job) {
    const { formatMessage } = this.props.intl;
    const assets = [];
    if (job?.blocks?.length > 0) {
      job.blocks.forEach(block => {
        const selectionAsset = block?.selection?.asset || {};
        // Empty pixel or Contingency selected
        if (
          ['EMPTY_FALLBACK', 'CONTINGENCY_IMAGE'].includes(
            block.selection.asset.key
          )
        ) {
          selectionAsset.image_link =
            block.selection.asset.key === 'CONTINGENCY_IMAGE'
              ? block.selection.context.rules.contingency_image
              : '/einstein/assets/images/empty.png';
          selectionAsset.key = formatMessage({
            id: 'components.ecs.selectionBrowser.assetDetails.noneSelected'
          });
          selectionAsset.type = block.asset_class.name;
          selectionAsset.asset_class_id = block.asset_class.uuid;
        }
        selectionAsset.isFallback =
          Object.values(block.selection.arms).every(x => x !== null) &&
          selectionAsset.uuid !== '-1';
        selectionAsset.clicked = block.selection.wasClicked;
        selectionAsset.subscriber = block.selection.subscriber_id;
        selectionAsset.isParent = block.isParent;
        selectionAsset.isChild = block.isChild;
        selectionAsset.parentBlock = block.parentBlock;
        selectionAsset.jobId = block.selection.job_id;
        selectionAsset.blockId = block.block_id;
        selectionAsset.uuid = '10000000-1000-4000-8000-100000000000'.replace(
          /[018]/g,
          c =>
            (
              c ^
              (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
            ).toString(16)
        );
        assets.push(selectionAsset);
      });
    }
    return assets;
  }

  drawSpinner(isLoading) {
    return isLoading ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  render() {
    const { job, isLoading } = this.props;
    const keys = [];
    const filteredAssets = this.filteredAssets();
    const assets = this.sortAssets(filteredAssets);

    const allowParentExpand = job?.blocks?.some(
      asset => asset.child_ids?.length
    );
    return (
      <React.Fragment>
        <AssetBrowser
          assets={assets}
          keys={keys}
          columns={this.columns()}
          showActions={false}
          onAction={this.handleTableAction.bind(this)}
          popoverOverflow={assets.length < 12}
          allowParentExpand={allowParentExpand}
          expandedRows={this.state.expandedAssets}
          className="selections"
        />
        {this.drawSpinner(isLoading)}
      </React.Fragment>
    );
  }
}

AssetTable.contextTypes = {
  router: PropTypes.object
};

AssetTable.propTypes = {
  actions: PropTypes.object,
  history: PropTypes.object.isRequired,
  job: PropTypes.object,
  subscriber: PropTypes.string,
  isLoading: PropTypes.bool,
  location: PropTypes.object
};

export default injectIntl(AssetTable);
